import {
  ChatConversation,
  ChatConversationParticipant,
  ChatMessageWithSender,
} from "@/libs/api/types";

type Conversation = {
  conversationId: string;
  messages: ChatMessageWithSender[];
  senderId: string;
  participants?: ChatConversationParticipant[];
};

export type ChatState = {
  conversations: Map<string, ChatConversation>;
  currentConversation: Conversation | null;
  loading: boolean;
  error: string | null;
};

export const initialState: ChatState = {
  conversations: new Map<string, ChatConversation>(),
  currentConversation: null,
  loading: false,
  error: null,
};

export type ChatContextAction =
  | { type: "setConversations"; payload: ChatConversation[] }
  | { type: "setCurrentConversation"; payload: Conversation }
  | { type: "addConversation"; payload: ChatConversation }
  | { type: "addMessage"; payload: ChatMessageWithSender }
  | { type: "appendMessages"; payload: ChatMessageWithSender[] }
  | {
      type: "updateConversation";
      payload: {
        conversationId: string;
        updates: Partial<ChatConversation>;
      };
    }
  | {
      type: "markMessageAsRead";
      payload: {
        messageId: string;
        conversationId: string;
      };
    }
  | {
      type: "deleteMessage";
      payload: {
        messageId: string;
        conversationId: string;
      };
    }
  | { type: "loading"; payload: boolean }
  | { type: "error"; payload: string | null };

export function chatReducer(
  state: ChatState,
  action: ChatContextAction,
): ChatState {
  switch (action.type) {
    case "setConversations":
      return handleSetConversations(state, action.payload);

    case "setCurrentConversation":
      return handleSetCurrentConversation(state, action.payload);

    case "addConversation":
      return handleAddConversation(state, action.payload);

    case "loading":
      return handleLoading(state, action.payload);

    case "addMessage":
      return handleAddMessage(state, action.payload);

    case "appendMessages":
      return handleAppendMessages(state, action.payload);

    case "updateConversation":
      return handleUpdateConversation(
        state,
        action.payload.conversationId,
        action.payload.updates,
      );

    case "markMessageAsRead":
      return handleMarkMessageAsRead(
        state,
        action.payload.messageId,
        action.payload.conversationId,
      );

    case "deleteMessage":
      return handleDeleteMessage(
        state,
        action.payload.messageId,
        action.payload.conversationId,
      );

    case "error":
      return handleError(state, action.payload);

    default:
      return state;
  }
}

function handleSetConversations(
  state: ChatState,
  conversations: ChatConversation[],
): ChatState {
  const conversationsMap = new Map<string, ChatConversation>();
  conversations.forEach((conversation) => {
    if (!conversation || !conversation.id) {
      return;
    }

    conversationsMap.set(conversation.id, conversation);
  });

  return {
    ...state,
    conversations: conversationsMap,
    loading: false,
  };
}

function handleSetCurrentConversation(
  state: ChatState,
  conversation: Conversation,
): ChatState {
  return {
    ...state,
    currentConversation: conversation,
    loading: false,
    error: null,
  };
}

function handleAddConversation(
  state: ChatState,
  conversation: ChatConversation,
): ChatState {
  const updatedConversations = new Map(state.conversations);
  updatedConversations.set(conversation.id, conversation);

  return {
    ...state,
    conversations: updatedConversations,
  };
}

function handleLoading(state: ChatState, loading: boolean): ChatState {
  return {
    ...state,
    loading,
  };
}

function handleAddMessage(
  state: ChatState,
  message: ChatMessageWithSender,
): ChatState {
  const isCurrentConversation =
    state.currentConversation?.conversationId === message.conversationId;

  const updatedCurrentConversation = isCurrentConversation
    ? {
        ...state.currentConversation!,
        messages: [...state.currentConversation!.messages, message],
      }
    : state.currentConversation;

  const conversation = state.conversations.get(message.conversationId);
  if (!conversation) {
    return { ...state, currentConversation: updatedCurrentConversation };
  }

  const updatedConversations = new Map(state.conversations);
  updatedConversations.set(message.conversationId, {
    ...conversation,
    lastMessage: message,
  });

  return {
    ...state,
    currentConversation: updatedCurrentConversation,
    conversations: updatedConversations,
  };
}

function handleAppendMessages(
  state: ChatState,
  messages: ChatMessageWithSender[],
): ChatState {
  if (!state.currentConversation) {
    return state;
  }
  return {
    ...state,
    currentConversation: {
      ...state.currentConversation,
      messages: [...messages, ...state.currentConversation.messages],
    },
  };
}

function handleUpdateConversation(
  state: ChatState,
  conversationId: string,
  updates: Partial<ChatConversation>,
): ChatState {
  const conversation = state.conversations.get(conversationId);

  if (!conversation) {
    return state;
  }

  const updatedConversations = new Map(state.conversations);
  updatedConversations.set(conversationId, {
    ...conversation,
    ...updates,
  });

  return {
    ...state,
    conversations: updatedConversations,
  };
}

function handleMarkMessageAsRead(
  state: ChatState,
  messageId: string,
  conversationId: string,
): ChatState {
  if (
    !state.currentConversation ||
    state.currentConversation.conversationId !== conversationId
  ) {
    return state;
  }

  const updatedMessages = state.currentConversation.messages.map((msg) =>
    msg.id === messageId ? { ...msg, isRead: true } : msg,
  );

  return {
    ...state,
    currentConversation: {
      ...state.currentConversation,
      messages: updatedMessages,
    },
  };
}

function handleDeleteMessage(
  state: ChatState,
  messageId: string,
  conversationId: string,
): ChatState {
  if (
    !state.currentConversation ||
    state.currentConversation.conversationId !== conversationId
  ) {
    return state;
  }

  const updatedMessages = state.currentConversation.messages.filter(
    (msg) => msg.id !== messageId,
  );

  return {
    ...state,
    currentConversation: {
      ...state.currentConversation,
      messages: updatedMessages,
    },
  };
}

function handleError(state: ChatState, error: string | null): ChatState {
  return {
    ...state,
    error,
    loading: false,
  };
}
